import {
  X,
  Truck,
  Calendar,
  User,
  Package,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  FileText,
  Building2,
  Camera,
  QrCode,
  Smartphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {useState} from 'react';
import { Button } from '@/components/ui/button';
import {
  QRCodeDisplay, getReceivingQRUrl 
} from '@/components/ui/qr-code-display';
import { cn } from '@/lib/classnames';
import type { Delivery } from '../../types';
import {
  DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS 
} from '../../types';
import { DeliveryTimeline } from './DeliveryTimeline';
import { LinesTable } from './LinesTable';

interface DeliveryDetailModalProps {
  readonly delivery: Delivery | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onStartReceiving?: (deliveryId: string) => void;
  readonly onMarkComplete?: (deliveryId: string) => void;
  readonly onReportIssue?: (delivery: Delivery) => void;
}

export function DeliveryDetailModal({
  delivery,
  isOpen,
  onClose,
  onStartReceiving,
  onMarkComplete,
  onReportIssue,
}: DeliveryDetailModalProps) {
  const navigate = useNavigate();
  const [showQRCode, setShowQRCode] = useState(false);

  if (!isOpen || !delivery) return null;

  const scheduledDate = new Date(delivery.scheduledDate);
  const isOverdue = scheduledDate < new Date() && delivery.status === 'scheduled';
  const progress =
    delivery.totalLines > 0
      ? Math.round((delivery.receivedLines / delivery.totalLines) * 100)
      : 0;
  const canStartReceiving = delivery.status === 'scheduled';
  const canComplete = delivery.status === 'in-progress';
  const canReportIssue =
    delivery.status === 'completed' || delivery.status === 'partial';
  const canUseCamera =
    delivery.status === 'scheduled' || delivery.status === 'in-progress';

  const handleReceiveWithCamera = () => {
    onClose();
    navigate(`/receiving/goods/${delivery.poNumber}`);
  };

  const receivingUrl = getReceivingQRUrl(delivery.poNumber);

  // Show QR code view for mobile handoff
  if (showQRCode) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-md rounded-lg bg-background shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Scan to Receive on Mobile</h2>
            <button
              type="button"
              onClick={() => setShowQRCode(false)}
              className="rounded-full p-2 hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <QRCodeDisplay
            value={receivingUrl}
            size={250}
            title={delivery.poNumber}
            subtitle={`${delivery.supplierName} • ${delivery.totalLines} items`}
            showActions
          />

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Mobile Receiving Workflow</p>
                <ul className="mt-2 space-y-1 text-blue-700">
                  <li>• Scan QR code with your phone camera</li>
                  <li>• Check off items as they arrive</li>
                  <li>• Take photos for AI quality inspection</li>
                  <li>• Upload invoice for auto-matching</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowQRCode(false)}
            >
              Back to Details
            </Button>
            <Button
              className="flex-1"
              onClick={handleReceiveWithCamera}
            >
              Open on This Device
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        <ModalHeader delivery={delivery} onClose={onClose} />
        <ModalContent
          delivery={delivery}
          scheduledDate={scheduledDate}
          isOverdue={isOverdue}
          progress={progress}
        />
        <ModalFooter
          delivery={delivery}
          canStartReceiving={canStartReceiving}
          canComplete={canComplete}
          canReportIssue={canReportIssue}
          canUseCamera={canUseCamera}
          onClose={onClose}
          onStartReceiving={onStartReceiving}
          onMarkComplete={onMarkComplete}
          onReportIssue={onReportIssue}
          onReceiveWithCamera={handleReceiveWithCamera}
          onShowQRCode={() => setShowQRCode(true)}
        />
      </div>
    </div>
  );
}

function ModalHeader({
  delivery,
  onClose,
}: {
  readonly delivery: Delivery;
  readonly onClose: () => void;
}) {
  return (
    <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
      <div className="flex items-center gap-4">
        <div className="rounded-lg bg-primary/10 p-3">
          <Truck className="h-6 w-6 text-primary" />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">{delivery.poNumber}</h2>
            <span
              className={cn(
                'rounded-full px-3 py-1 text-sm font-medium',
                DELIVERY_STATUS_COLORS[delivery.status]
              )}
            >
              {DELIVERY_STATUS_LABELS[delivery.status]}
            </span>
          </div>
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4" />
            {delivery.supplierName}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-full p-2 hover:bg-muted"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function ModalContent({
  delivery,
  scheduledDate,
  isOverdue,
  progress,
}: {
  readonly delivery: Delivery;
  readonly scheduledDate: Date;
  readonly isOverdue: boolean;
  readonly progress: number;
}) {
  return (
    <div className="p-6 space-y-6">
      <SummaryCards
        delivery={delivery}
        scheduledDate={scheduledDate}
        isOverdue={isOverdue}
      />
      <ProgressBar progress={progress} />
      <div className="rounded-lg border p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold">
          <ArrowRight className="h-5 w-5" />
          Delivery Flow
        </h3>
        <DeliveryTimeline status={delivery.status} />
      </div>
      {delivery.notes && (
        <div className="rounded-lg border p-4">
          <h3 className="mb-2 flex items-center gap-2 font-semibold">
            <FileText className="h-5 w-5" />
            Notes
          </h3>
          <p className="text-sm text-gray-600">{delivery.notes}</p>
        </div>
      )}
      <div>
        <h3 className="mb-4 flex items-center gap-2 font-semibold">
          <Package className="h-5 w-5" />
          Delivery Lines ({delivery.lines.length})
        </h3>
        <LinesTable lines={delivery.lines} />
      </div>
    </div>
  );
}

function SummaryCards({
  delivery,
  scheduledDate,
  isOverdue,
}: {
  readonly delivery: Delivery;
  readonly scheduledDate: Date;
  readonly isOverdue: boolean;
}) {
  return (
    <div className="grid grid-cols-4 gap-4">
      <div
        className={cn(
          'rounded-lg border p-4',
          isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-50'
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 text-sm',
            isOverdue ? 'text-red-600' : 'text-gray-500'
          )}
        >
          <Calendar className="h-4 w-4" />
          Scheduled
        </div>
        <p className={cn('mt-1 font-medium', isOverdue && 'text-red-700')}>
          {scheduledDate.toLocaleDateString()}
        </p>
        {isOverdue && (
          <p className="text-xs text-red-600 font-medium">Overdue</p>
        )}
      </div>
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Package className="h-4 w-4" />
          Total Lines
        </div>
        <p className="mt-1 text-2xl font-bold">{delivery.totalLines}</p>
      </div>
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle className="h-4 w-4" />
          Received
        </div>
        <p className="mt-1 text-2xl font-bold text-green-600">
          {delivery.receivedLines}
        </p>
      </div>
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <User className="h-4 w-4" />
          Received By
        </div>
        {delivery.receivedByName ? (
          <p className="mt-1 font-medium">{delivery.receivedByName}</p>
        ) : (
          <p className="mt-1 text-gray-400">Not started</p>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ progress }: { readonly progress: number }) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Receiving Progress</span>
        <span className="text-sm text-muted-foreground">{progress}%</span>
      </div>
      <div className="h-3 w-full rounded-full bg-gray-200">
        <div
          className={cn(
            'h-3 rounded-full transition-all',
            progress === 100 ? 'bg-green-500' : 'bg-primary'
          )}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

interface ModalFooterProps {
  readonly delivery: Delivery;
  readonly canStartReceiving: boolean;
  readonly canComplete: boolean;
  readonly canReportIssue: boolean;
  readonly canUseCamera: boolean;
  readonly onClose: () => void;
  readonly onStartReceiving?: (deliveryId: string) => void;
  readonly onMarkComplete?: (deliveryId: string) => void;
  readonly onReportIssue?: (delivery: Delivery) => void;
  readonly onReceiveWithCamera: () => void;
  readonly onShowQRCode: () => void;
}

function ModalFooter({
  delivery,
  canStartReceiving,
  canComplete,
  canReportIssue,
  canUseCamera,
  onClose,
  onStartReceiving,
  onMarkComplete,
  onReportIssue,
  onReceiveWithCamera,
  onShowQRCode,
}: ModalFooterProps) {
  return (
    <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-background p-6">
      <Button variant="outline" onClick={onClose}>
        Close
      </Button>
      {canReportIssue && onReportIssue && (
        <Button
          variant="outline"
          className="text-amber-600 hover:bg-amber-50"
          onClick={() => onReportIssue(delivery)}
        >
          <AlertCircle className="mr-2 h-4 w-4" />
          Report Issue
        </Button>
      )}
      {canUseCamera && (
        <>
          <Button
            variant="outline"
            className="text-purple-600 hover:bg-purple-50"
            onClick={onShowQRCode}
          >
            <QrCode className="mr-2 h-4 w-4" />
            Scan to Mobile
          </Button>
          <Button
            variant="outline"
            className="text-blue-600 hover:bg-blue-50"
            onClick={onReceiveWithCamera}
          >
            <Camera className="mr-2 h-4 w-4" />
            Receive with Camera
          </Button>
        </>
      )}
      {canStartReceiving && onStartReceiving && (
        <Button onClick={() => onStartReceiving(delivery.deliveryId)}>
          <Truck className="mr-2 h-4 w-4" />
          Start Receiving
        </Button>
      )}
      {canComplete && onMarkComplete && (
        <Button onClick={() => onMarkComplete(delivery.deliveryId)}>
          <CheckCircle className="mr-2 h-4 w-4" />
          Mark Complete
        </Button>
      )}
    </div>
  );
}
